import React, {useEffect, useState} from 'react'
import {expectedKeys, InvoiceData , InvoiceFormValues, OptionsRes, RulesRes, SelectOption, InvoiceStatus} from '../types'
import * as Yup from 'yup'
import {Formik} from 'formik'
import {Col, Form, Row, Spinner} from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import {ErrorsFound} from '../services/knownErrorHandling'
import {useShowError} from '../hooks/useShowError'
import Selector from '../ui/Selector'

interface Props {
	invoice:InvoiceData
	fetch: (req: { data: InvoiceFormValues, id: number }) => Promise<void>
	isFetching: boolean
	isSuccess: boolean
	errorsFromServer:  ErrorsFound | null
	options: OptionsRes
	rules: RulesRes
}

const InvoiceForm: React.FC<Props> = ({invoice, fetch, isFetching, isSuccess, errorsFromServer, options, rules}) => {
	const [isSuccessBtn, setIsSuccessBtn] = useState(false)
	const [isHandled, setIsHandled] = useState(false)
	const getInitialValues = () : InvoiceFormValues => {return ({
		supplier: invoice.supplier ?? '',
		buyer: invoice.buyer ?? '',
		number: invoice.number ?? '',
		date_of_sale: (invoice.date_of_sale ?? ''),
		due_date: invoice.due_date ?? '',
		paid_on: invoice.paid_on ?? '',
		total_amount: invoice.total_amount ?? '',
		subtotal_amount: invoice.subtotal_amount ?? '',
		vat: invoice.vat ?? '',
		currency: invoice.currency ?? '',
		iban: invoice.iban ?? '',
		cash_register: invoice.cash_register ?? '',
		expense_item: invoice.expense_item ?? '',
		activity_area: invoice.activity_area ?? '',
		division: invoice.division ?? '',
		project: invoice.project ?? '',
		notes: invoice.notes ?? '',
	})}

	const [initialValues, setInitialValues] = useState<InvoiceFormValues>(getInitialValues())

	useEffect(() => {
		if(!isFetching && isSuccess && isHandled) {
			setIsHandled(false)
			setIsSuccessBtn(true)
			setTimeout(() => {
				setIsSuccessBtn(false)
			}, 3000)
		}
	}, [isFetching])

	useEffect(() => {
		setInitialValues(getInitialValues())
	}, [invoice])

	const validationSchema = Yup.object({
		supplier: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		buyer: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		number: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		date_of_sale: Yup.date().nullable().typeError('Має бути датою'),
		due_date: Yup.date().nullable().typeError('Має бути датою'),
		paid_on: Yup.date().nullable().typeError('Має бути датою'),
		total_amount: Yup.number().nullable().typeError('Має бути числом').test('total_amount', 'Має бути числом з максимумом 13 цифр і опціонально 2 десятковими цифрами', (value) => value === null || /^\d{1,13}(\.\d{1,2})?$/.test(`${value}`)),
		subtotal_amount: Yup.number().nullable().typeError('Має бути числом').test('subtotal_amount', 'Має бути числом з максимумом 13 цифр і опціонально 2 десятковими цифрами', (value) => value === null || /^\d{1,13}(\.\d{1,2})?$/.test(`${value}`)),
		vat: Yup.number().nullable().typeError('Має бути числом').test('vat', 'Має бути числом з максимумом 13 цифр і опціонально 2 десятковими цифрами', (value) => value === null || /^\d{1,13}(\.\d{1,2})?$/.test(`${value}`)),
		currency: Yup.string().nullable().max(3, 'Максимальна довжина – 3 символи'),
		iban: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		cash_register: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		expense_item: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		activity_area: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		division: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		project: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів'),
		notes: Yup.string().nullable().max(255, 'Максимальна довжина – 255 символів')

	})

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values) => {
				fetch({ data: values, id: invoice.id })
				setIsHandled(true)
			}}
			validationSchema={validationSchema}
		>
			{({
				values,
				errors,
				touched,
				handleChange,
				handleBlur,
				handleSubmit,
				setValues,
				setErrors,
				resetForm,
				isValid,
				setFieldValue
			}) => {
				const [isErrorFromServer, setIsErrorFromServer] = useState(false)
				const isErrorSupplier = useShowError(errors.supplier, touched.supplier, isErrorFromServer)
				const isErrorBuyer = useShowError(errors.buyer, touched.buyer, isErrorFromServer)
				const isErrorNumber = useShowError(errors.number, touched.number, isErrorFromServer)
				const isErrorDateOfSale = useShowError(errors.date_of_sale, touched.date_of_sale, isErrorFromServer)
				const isErrorDueDate = useShowError(errors.due_date, touched.due_date, isErrorFromServer)
				const isErrorPaidOn = useShowError(errors.paid_on, touched.paid_on, isErrorFromServer)
				const isErrorTotalAmount = useShowError(errors.total_amount, touched.total_amount, isErrorFromServer)
				const isErrorSubtotalAmount = useShowError(errors.subtotal_amount, touched.subtotal_amount, isErrorFromServer)
				const isErrorVat = useShowError(errors.vat, touched.vat, isErrorFromServer)
				const isErrorCurrency = useShowError(errors.currency, touched.currency, isErrorFromServer)
				const isErrorIban = useShowError(errors.iban, touched.iban, isErrorFromServer)
				const isErrorCashRegister = useShowError(errors.cash_register, touched.cash_register, isErrorFromServer)
				const isErrorExpenseItem = useShowError(errors.expense_item, touched.expense_item, isErrorFromServer)
				const isErrorActivityArea = useShowError(errors.activity_area, touched.activity_area, isErrorFromServer)
				const isErrorDivision = useShowError(errors.division, touched.division, isErrorFromServer)
				const isErrorProject = useShowError(errors.project, touched.project, isErrorFromServer)
				const isErrorNotes = useShowError(errors.notes, touched.notes, isErrorFromServer)
				const [cashRegistersOptions, setCashRegistersOptions] = useState<SelectOption[]>([])
				const [expenseItemsOptions, setExpenseItemsOptions] = useState<SelectOption[]>([])
				const [activityAreasOptions, setActivityAreasOptions] = useState<SelectOption[]>([])
				const [divisionOptions, setDivisionOptions] = useState<SelectOption[]>([])
				const [projectOptions, setProjectOptions] = useState<SelectOption[]>([])
				const [cashRegisterDisplayValue, setCashRegisterDisplayValue] = useState<null | SelectOption>(null)
				const [expenseItemDisplayValue, setExpenseItemDisplayValue] = useState<null | SelectOption>(null)
				const [activityAreaDisplayValue, setActivityAreaDisplayValue] = useState<null | SelectOption>(null)
				const [divisionDisplayValue, setDivisionDisplayValue] = useState<null | SelectOption>(null)
				const [projectDisplayValue, setProjectDisplayValue] = useState<null | SelectOption>(null)

				const setOptionsFunctions = {
					cash_registers: setCashRegistersOptions,
					expense_items: setExpenseItemsOptions,
					activity_areas: setActivityAreasOptions,
					divisions: setDivisionOptions,
					projects: setProjectOptions
				}

				const setDisplayValueFunctions = {
					cash_registers: setCashRegisterDisplayValue,
					expense_items: setExpenseItemDisplayValue,
					activity_areas: setActivityAreaDisplayValue,
					divisions: setDivisionDisplayValue,
					projects: setProjectDisplayValue
				}

				const setOptions = () => {
					Object.keys(setOptionsFunctions).forEach((key) => {
						const typedKey = key as keyof typeof setOptionsFunctions
						const availableOptions = Object.keys(options[typedKey]).map(code => ({
							value: code,
							label: options[typedKey][code]?.full_name ?? `помилка коду (${code})`
						}))
						setOptionsFunctions[typedKey](availableOptions)
					})
				}

				const checkRules = (option: keyof typeof expectedKeys) => {
					const key = expectedKeys[option] as keyof InvoiceFormValues
					const value = values[key]
					if(value) {
						const typedKey = option as keyof typeof setDisplayValueFunctions
						setDisplayValueFunctions[typedKey](
							{
								value: values[key] as string,
								label: options[typedKey][value]?.full_name ?? `помилка коду (${value})`
							}
						)
					}

					if (value && rules[option] && rules[option][value]) {

						Object.keys(rules[option][value]).forEach((ruleOption) => {
							const ruleKey = ruleOption as keyof typeof setOptionsFunctions
							setOptionsFunctions[ruleKey](
								rules[option][value][ruleOption].map((item) => ({
									value: item,
									label: options[ruleKey][item]?.full_name ?? `помилка коду (${item})`
								}))
							)
						})
					}
				}

				const resetOptions = () => {
					setOptions()
					Object.keys(expectedKeys).forEach((option) => {
						checkRules(option as keyof typeof expectedKeys)
					})
				}


				useEffect(() => {
					resetOptions()
				}, [values.division, values.project, values.activity_area, values.expense_item, values.cash_register])


				useEffect(() => {
					if(errorsFromServer){
						setErrors(errorsFromServer)
					}
				}, [errorsFromServer])

				useEffect(() => {
					if(isValid && isErrorFromServer){
						setIsErrorFromServer(false)
					}
				},[errors])

				useEffect(() => {
					resetForm()
					setValues(getInitialValues())
				}, [invoice])

				if(options) {
					return (
						<Form noValidate onSubmit={handleSubmit} className='gap-3'>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Постачальник</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="supplier"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.supplier ?? ''}
										isInvalid={isErrorSupplier}
									/>
									<Form.Control.Feedback type="invalid">{errors.supplier}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Покупець</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="buyer"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.buyer ?? ''}
										isInvalid={isErrorBuyer}
									/>
									<Form.Control.Feedback type="invalid">{errors.buyer}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Номер</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="number"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.number ?? ''}
										isInvalid={isErrorNumber}
									/>
									<Form.Control.Feedback type="invalid">{errors.number}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Дата продажу</Form.Label>
								<Col>
									<Form.Control
										type="date"
										name="date_of_sale"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.date_of_sale ?? ''}
										isInvalid={isErrorDateOfSale}
									/>
									<Form.Control.Feedback type="invalid">{errors.date_of_sale}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Термін оплати</Form.Label>
								<Col>
									<Form.Control
										type="date"
										name="due_date"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.due_date ?? ''}
										isInvalid={isErrorDueDate}
									/>
									<Form.Control.Feedback type="invalid">{errors.due_date}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Дата оплати</Form.Label>
								<Col>
									<Form.Control
										type="date"
										name="paid_on"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.paid_on ?? ''}
										isInvalid={isErrorPaidOn}
									/>
									<Form.Control.Feedback type="invalid">{errors.paid_on}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Сума</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="total_amount"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.total_amount ?? ''}
										isInvalid={isErrorTotalAmount}
									/>
									<Form.Control.Feedback type="invalid">{errors.total_amount}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Сума без ПДВ</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="subtotal_amount"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.subtotal_amount ?? ''}
										isInvalid={isErrorSubtotalAmount}
									/>
									<Form.Control.Feedback
										type="invalid">{errors.subtotal_amount}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">ПДВ</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="vat"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.vat ?? ''}
										isInvalid={isErrorVat}
									/>
									<Form.Control.Feedback type="invalid">{errors.vat}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Валюта</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="currency"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.currency ?? ''}
										isInvalid={isErrorCurrency}
									/>
									<Form.Control.Feedback type="invalid">{errors.currency}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">IBAN</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="iban"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.iban ?? ''}
										isInvalid={isErrorIban}
									/>
									<Form.Control.Feedback type="invalid">{errors.iban}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">КАСА (з якої здійснено платіж)</Form.Label>
								<Col>
									<Selector
										isError={!!values.cash_register && !cashRegistersOptions.some(option => option.value === values.cash_register)}
										name="cash_register"
										value={cashRegisterDisplayValue}
										onChange={
											(value) => {
												if (value) {
													setFieldValue('cash_register', value.value)
												}
											}
										}
										onBlur={handleBlur}
										options={cashRegistersOptions}
									/>
									<Form.Control.Feedback type="invalid">{errors.cash_register}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Стаття витрат</Form.Label>
								<Col>
									<Selector
										isError={!!values.expense_item && !expenseItemsOptions.some(option => option.value === values.expense_item)}
										name="expense_item"
										value={expenseItemDisplayValue}
										onChange={
											(value) => setFieldValue('expense_item', value?.value ?? '')
										}
										onBlur={handleBlur}
										options={expenseItemsOptions}
									/>
									<Form.Control.Feedback type="invalid">{errors.expense_item}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Область діяльності</Form.Label>
								<Col>
									<Selector
										isError={ !!values.activity_area && !activityAreasOptions.some(option => option.value === values.activity_area)}
										name="activity_area"
										value={activityAreaDisplayValue}
										onChange={
											(value) => setFieldValue('activity_area', value?.value ?? '')
										}
										onBlur={handleBlur}
										options={activityAreasOptions}
									/>
									<Form.Control.Feedback type="invalid">{errors.activity_area}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Підрозділ</Form.Label>
								<Col>
									<Selector
										isError={!!values.division && !divisionOptions.some(option => option.value === values.division)}
										name="division"
										value={divisionDisplayValue}
										onChange={
											(value) => setFieldValue('division', value?.value ?? '')
										}
										onBlur={handleBlur}
										options={
											divisionOptions
										}
									/>
									<Form.Control.Feedback type="invalid">{errors.division}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">Проект</Form.Label>
								<Col>
									<Selector
										isError={!!values.project && !projectOptions.some(option => option.value === values.project)}
										name="project"
										value={projectDisplayValue}
										onChange={
											(value) => setFieldValue('project', value?.value ?? '')
										}
										onBlur={handleBlur}
										options={projectOptions}
									/>
									<Form.Control.Feedback type="invalid">{errors.project}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className="mb-3">
								<Form.Label column sm="4">ПРИМІТКИ - за що цей рахунок</Form.Label>
								<Col>
									<Form.Control
										type="text"
										name="notes"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.notes ?? ''}
										isInvalid={isErrorNotes}
									/>
									<Form.Control.Feedback type="invalid">{errors.notes}</Form.Control.Feedback>
								</Col>
							</Form.Group>
							<Button
								variant={isSuccessBtn ? 'success' : 'primary'}
								className='mt-3'
								type="submit"
								disabled={
									!!values.project && !projectOptions.some(option => option.value === values.project) ||
									!!values.division && !divisionOptions.some(option => option.value === values.division) ||
									!!values.activity_area && !activityAreasOptions.some(option => option.value === values.activity_area) ||
									!!values.expense_item && !expenseItemsOptions.some(option => option.value === values.expense_item) ||
									!!values.cash_register && !cashRegistersOptions.some(option => option.value === values.cash_register)
								}
							>
								{isFetching ?
									<>
										<Spinner
											className='me-1'
											as="span"
											animation="grow"
											size="sm"
											role="status"
											aria-hidden="true"
										/>
										Зберігання...
									</>
									: isSuccessBtn ? 'Заявка збережена' : 'Зберегти'
								}
							</Button>
						</Form>
					)
				}
			}}
		</Formik>
	)
}

export default InvoiceForm
